import { useState, useEffect } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ArrowsClockwise } from "@phosphor-icons/react";

/**
 * Reusable reverse pricing calculator dialog.
 * Opens pre-filled with the caller's markup A/B + VAT settings, lets the user enter
 * a target client price and shows the implied supplier cost (max budget).
 *
 * Props:
 *   open, onOpenChange — Dialog control
 *   initialMarkupA, initialMarkupB — initial markup percentages (numbers)
 *   initialMarkupALabel, initialMarkupBLabel — display labels
 *   initialVatEnabled, initialVatRate — initial VAT settings
 *   initialTotal — pre-filled target client price (e.g. existing supplier quote total)
 *   contextLabel — short string shown in the header (e.g. "QT-A1B2 · 10% + 30%")
 */
export default function ReverseCalcDialog({
  open,
  onOpenChange,
  initialMarkupA = 30,
  initialMarkupB = 10,
  initialMarkupALabel = "Our Mark Up",
  initialMarkupBLabel = "Client Mark Up",
  initialVatEnabled = false,
  initialVatRate = 20,
  initialTotal = 1000,
  contextLabel = "",
}) {
  const [mode, setMode] = useState("reverse");
  const [markupAPct, setMarkupAPct] = useState(initialMarkupA);
  const [markupBPct, setMarkupBPct] = useState(initialMarkupB);
  const [vatEnabled, setVatEnabled] = useState(initialVatEnabled);
  const [vatRate, setVatRate] = useState(initialVatRate);
  const [forwardCost, setForwardCost] = useState(initialTotal);
  const [reverseTotal, setReverseTotal] = useState(initialTotal);
  const [templates, setTemplates] = useState([]);

  // Re-sync state every time the dialog is opened with fresh props
  useEffect(() => {
    if (!open) return;
    setMarkupAPct(initialMarkupA);
    setMarkupBPct(initialMarkupB);
    setVatEnabled(initialVatEnabled);
    setVatRate(initialVatRate);
    setForwardCost(initialTotal);
    setReverseTotal(initialTotal);
    setMode("reverse");
    API.get("/markup-templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
  }, [open, initialMarkupA, initialMarkupB, initialVatEnabled, initialVatRate, initialTotal]);

  const aFactor = 1 + Number(markupAPct || 0) / 100;
  const bFactor = 1 + Number(markupBPct || 0) / 100;
  const vFactor = vatEnabled ? 1 + Number(vatRate || 0) / 100 : 1;

  const fwdCost = Number(forwardCost || 0);
  const fwdAfterA = fwdCost * aFactor;
  const fwdAAmount = fwdAfterA - fwdCost;
  const fwdAfterB = fwdAfterA * bFactor;
  const fwdBAmount = fwdAfterB - fwdAfterA;
  const fwdVatAmount = fwdAfterB * (vFactor - 1);
  const fwdTotal = fwdAfterB + fwdVatAmount;
  const fwdProfit = fwdAAmount + fwdBAmount;

  const revTotal = Number(reverseTotal || 0);
  const revAfterB = vFactor > 0 ? revTotal / vFactor : revTotal;
  const revVatAmount = revTotal - revAfterB;
  const revAfterA = bFactor > 0 ? revAfterB / bFactor : revAfterB;
  const revBAmount = revAfterB - revAfterA;
  const revCost = aFactor > 0 ? revAfterA / aFactor : revAfterA;
  const revAAmount = revAfterA - revCost;
  const revProfit = revAAmount + revBAmount;

  const fmt = (n) => `£${(Number(n) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleLoadTemplate = (id) => {
    const t = templates.find(t => t.id === id);
    if (!t) return;
    setMarkupAPct(t.markup_a_pct || 0);
    setMarkupBPct(t.markup_b_pct || 0);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reverse-calc-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowsClockwise size={18} /> Reverse Pricing Calculator
          </DialogTitle>
          {contextLabel && <p className="text-[11px] text-muted-foreground font-mono">{contextLabel}</p>}
        </DialogHeader>

        <div className="space-y-4">
          {/* Mode toggle */}
          <div className="inline-flex border border-border rounded-md overflow-hidden text-xs" data-testid="rcd-mode-toggle">
            <button
              type="button"
              onClick={() => setMode("forward")}
              className={`px-3 py-1.5 ${mode === "forward" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
              data-testid="rcd-mode-forward"
            >Forward</button>
            <button
              type="button"
              onClick={() => setMode("reverse")}
              className={`px-3 py-1.5 border-l border-border ${mode === "reverse" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
              data-testid="rcd-mode-reverse"
            >Reverse (max budget)</button>
          </div>

          {/* Inputs */}
          <div className="border border-border rounded-lg p-3 space-y-3 bg-muted/10">
            {templates.length > 0 && (
              <div>
                <Label className="text-xs">Load from template</Label>
                <select
                  onChange={(e) => handleLoadTemplate(e.target.value)}
                  className="w-full mt-1 h-8 px-2 rounded-md border border-input bg-background text-xs"
                  data-testid="rcd-template-select"
                >
                  <option value="">— Use markup from this quote —</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.name} ({t.markup_a_pct}% + {t.markup_b_pct}%)</option>
                  ))}
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">{initialMarkupALabel} (A)</Label>
                <div className="relative mt-1">
                  <Input type="number" step="0.5" value={markupAPct} onChange={e => setMarkupAPct(e.target.value)} className="pr-7 h-8 text-sm" data-testid="rcd-markup-a" />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                </div>
              </div>
              <div>
                <Label className="text-xs">{initialMarkupBLabel} (B)</Label>
                <div className="relative mt-1">
                  <Input type="number" step="0.5" value={markupBPct} onChange={e => setMarkupBPct(e.target.value)} className="pr-7 h-8 text-sm" data-testid="rcd-markup-b" />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <Switch checked={vatEnabled} onCheckedChange={setVatEnabled} data-testid="rcd-vat-toggle" />
                <span>VAT</span>
              </label>
              {vatEnabled && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-muted-foreground">Rate</span>
                  <div className="relative w-20">
                    <Input type="number" step="0.5" value={vatRate} onChange={e => setVatRate(e.target.value)} className="pr-6 h-7 text-xs" data-testid="rcd-vat-rate" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground">%</span>
                  </div>
                </div>
              )}
            </div>
            {mode === "forward" ? (
              <div>
                <Label className="text-xs">Supplier cost</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">£</span>
                  <Input type="number" step="0.01" value={forwardCost} onChange={e => setForwardCost(e.target.value)} className="pl-6 h-9 font-[Chivo]" data-testid="rcd-forward-cost" />
                </div>
              </div>
            ) : (
              <div>
                <Label className="text-xs">Target client price {vatEnabled ? "(inc. VAT)" : "(ex. VAT)"}</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">£</span>
                  <Input type="number" step="0.01" value={reverseTotal} onChange={e => setReverseTotal(e.target.value)} className="pl-6 h-9 font-[Chivo]" data-testid="rcd-reverse-total" />
                </div>
              </div>
            )}
          </div>

          {/* Breakdown */}
          <div className="border border-border rounded-lg p-3" data-testid="rcd-breakdown">
            {mode === "forward" ? (
              <table className="w-full text-xs">
                <tbody className="divide-y divide-border">
                  <tr><td className="py-1.5 text-muted-foreground">Supplier cost</td><td className="py-1.5 text-right font-mono tabular-nums" data-testid="rcd-fwd-cost">{fmt(fwdCost)}</td></tr>
                  <tr><td className="py-1.5 text-muted-foreground">+ {initialMarkupALabel} ({markupAPct}%)</td><td className="py-1.5 text-right font-mono tabular-nums text-primary">{fmt(fwdAAmount)}</td></tr>
                  <tr><td className="py-1.5 text-muted-foreground">+ {initialMarkupBLabel} ({markupBPct}%)</td><td className="py-1.5 text-right font-mono tabular-nums text-violet-600 dark:text-violet-400">{fmt(fwdBAmount)}</td></tr>
                  {vatEnabled && (
                    <tr><td className="py-1.5 text-muted-foreground">+ VAT ({vatRate}%)</td><td className="py-1.5 text-right font-mono tabular-nums">{fmt(fwdVatAmount)}</td></tr>
                  )}
                  <tr className="border-t-2 border-border bg-muted/30">
                    <td className="py-2 px-2 font-semibold">Client price</td>
                    <td className="py-2 px-2 text-right font-mono tabular-nums text-sm font-bold font-[Chivo]" data-testid="rcd-fwd-total">{fmt(fwdTotal)}</td>
                  </tr>
                  <tr className="bg-emerald-50/50 dark:bg-emerald-900/10">
                    <td className="py-1.5 px-2 text-emerald-700 dark:text-emerald-400">Profit</td>
                    <td className="py-1.5 px-2 text-right font-mono tabular-nums text-emerald-700 dark:text-emerald-400 font-semibold">{fmt(fwdProfit)}</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <table className="w-full text-xs">
                <tbody className="divide-y divide-border">
                  <tr className="bg-muted/30">
                    <td className="py-2 px-2 font-semibold">Target client price</td>
                    <td className="py-2 px-2 text-right font-mono tabular-nums text-sm font-bold font-[Chivo]" data-testid="rcd-rev-total">{fmt(revTotal)}</td>
                  </tr>
                  {vatEnabled && <tr><td className="py-1.5 text-muted-foreground">− VAT ({vatRate}%)</td><td className="py-1.5 text-right font-mono tabular-nums">{fmt(revVatAmount)}</td></tr>}
                  <tr><td className="py-1.5 text-muted-foreground">− {initialMarkupBLabel} ({markupBPct}%)</td><td className="py-1.5 text-right font-mono tabular-nums text-violet-600 dark:text-violet-400">{fmt(revBAmount)}</td></tr>
                  <tr><td className="py-1.5 text-muted-foreground">− {initialMarkupALabel} ({markupAPct}%)</td><td className="py-1.5 text-right font-mono tabular-nums text-primary">{fmt(revAAmount)}</td></tr>
                  <tr className="border-t-2 border-border bg-emerald-50/40 dark:bg-emerald-900/20">
                    <td className="py-2 px-2 font-semibold text-emerald-800 dark:text-emerald-300">Implied supplier cost (max budget)</td>
                    <td className="py-2 px-2 text-right font-mono tabular-nums text-sm font-bold font-[Chivo] text-emerald-800 dark:text-emerald-300" data-testid="rcd-rev-cost">{fmt(revCost)}</td>
                  </tr>
                  <tr>
                    <td className="py-1.5 px-2 text-muted-foreground text-[11px]">Profit at this price</td>
                    <td className="py-1.5 px-2 text-right font-mono tabular-nums text-emerald-700 dark:text-emerald-400 text-[11px] font-semibold">{fmt(revProfit)}</td>
                  </tr>
                </tbody>
              </table>
            )}
            {mode === "reverse" && (
              <p className="text-[10px] text-muted-foreground italic mt-2">
                The implied cost is the max you can pay your supplier and still hit this client price.
              </p>
            )}
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="rcd-close">Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
